import { ASSET_KIND_LABELS, ASSET_KINDS, type AssetKind } from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import { Alert, Card, DateRangePicker, FormField, MoneyText, Select, SkeletonRows } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatHours } from "../../../features/maintenance/maintenance-values.js";
import { useMaintenanceReport } from "../../../features/maintenance/queries.js";
import { MaintenancePage, StationFilterField } from "../../../features/maintenance/shared.js";
import { ExportButtons, StatTile, TableScroll } from "../../../features/reports/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount } from "../../../lib/format.js";
import { usePermissions } from "../../../lib/session.js";

export const Route = createFileRoute("/_app/bakim/raporlar")({ component: MaintenanceReportRoute });

/**
 * Bakım raporu (Prompt 23 web, `GET /maintenance/reports`): istasyon × varlık — arıza sayısı, önleyici bakım, açık iş emri,
 * SLA aşımı, ortalama onarım süresi, parça / işçilik / toplam maliyet. Rapor merkezi izni varsa Excel / PDF dışa aktarımı
 * (`MAINTENANCE` anahtarı; zamanlanabilir).
 */
function MaintenanceReportRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MaintenancePage title={t("menu.maintenanceReports")} description={t("maintenance.reports.description")}>
      <ReportBody />
    </MaintenancePage>
  );
}

function ReportBody(): ReactElement {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const today = toIstanbulDate(new Date());
  const [range, setRange] = useState<{ from: LocalDate | null; to: LocalDate | null }>({
    from: addDays(today, -29),
    to: today,
  });
  const [stationId, setStationId] = useState<string | null>(null);
  const [assetKind, setAssetKind] = useState<AssetKind | null>(null);
  const ready = range.from !== null && range.to !== null && range.from <= range.to;
  const report = useMaintenanceReport(
    { from: range.from ?? today, to: range.to ?? today, stationId, assetKind },
    ready,
  );
  const data = report.data;
  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <DateRangePicker value={range} onValueChange={setRange} label={t("maintenance.fields.period")} />
        <StationFilterField value={stationId} onChange={setStationId} />
        <FormField label={t("maintenance.fields.assetKind")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={assetKind ?? ""}
              onChange={(event) => setAssetKind((event.target.value || null) as AssetKind | null)}
            >
              <option value="">{t("common.all")}</option>
              {ASSET_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {ASSET_KIND_LABELS[kind]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        {permissions.has("reports.read") && ready ? (
          <ExportButtons
            reportKey="MAINTENANCE"
            state={{
              range: { from: range.from!, to: range.to! },
              preset: null,
              companyId: null,
              stationIds: [],
              stationId,
            }}
          />
        ) : null}
      </Card>
      {report.error ? (
        <Alert tone="danger">{errorMessage(report.error)}</Alert>
      ) : !data ? (
        <SkeletonRows rows={6} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatTile label={t("maintenance.reports.failures")} value={formatCount(data.totals.failures)} />
            <StatTile label={t("maintenance.reports.mttr")} value={formatHours(data.totals.mttrHours)} />
            <StatTile
              label={t("maintenance.reports.preventiveDone")}
              value={formatCount(data.totals.preventiveDone)}
            />
            <StatTile
              label={t("maintenance.reports.slaBreaches")}
              value={formatCount(data.totals.slaBreaches)}
            />
            <StatTile
              label={t("maintenance.reports.totalCost")}
              value={<MoneyText value={data.totals.total} />}
            />
          </div>
          <Card className="grid gap-3 p-4">
            <h2 className="text-base font-semibold">{t("maintenance.reports.byAsset")}</h2>
            {data.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("maintenance.reports.empty")}</p>
            ) : (
              <TableScroll label={t("maintenance.reports.byAsset")}>
                <table className="w-full text-sm">
                  <caption className="sr-only">{t("maintenance.reports.byAsset")}</caption>
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th scope="col" className="px-3 py-2">
                        {t("maintenance.fields.station")}
                      </th>
                      <th scope="col" className="px-3 py-2">
                        {t("maintenance.fields.asset")}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right">
                        {t("maintenance.reports.failures")}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right">
                        {t("maintenance.reports.preventiveDone")}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right">
                        {t("maintenance.reports.open")}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right">
                        {t("maintenance.reports.slaBreaches")}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right">
                        {t("maintenance.reports.mttr")}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right">
                        {t("maintenance.reports.parts")}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right">
                        {t("maintenance.reports.labor")}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right">
                        {t("maintenance.reports.totalCost")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr
                        key={`${row.stationId}|${row.assetKind ?? ""}|${row.assetId ?? ""}`}
                        className="border-b last:border-0"
                      >
                        <td className="px-3 py-2 whitespace-nowrap">{row.stationCode}</td>
                        <td className="px-3 py-2">
                          {row.assetName}
                          {row.assetKind ? (
                            <span className="text-xs text-muted-foreground">
                              {" "}
                              · {ASSET_KIND_LABELS[row.assetKind]}
                            </span>
                          ) : null}
                        </td>
                        <td className="tabular px-3 py-2 text-right">{formatCount(row.failures)}</td>
                        <td className="tabular px-3 py-2 text-right">{formatCount(row.preventiveDone)}</td>
                        <td className="tabular px-3 py-2 text-right">{formatCount(row.openWorkOrders)}</td>
                        <td className="tabular px-3 py-2 text-right">{formatCount(row.slaBreaches)}</td>
                        <td className="tabular px-3 py-2 text-right">{formatHours(row.mttrHours)}</td>
                        <td className="px-3 py-2 text-right">
                          <MoneyText value={row.parts} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <MoneyText value={row.labor} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <MoneyText value={row.total} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
            <p className="text-xs text-muted-foreground">{t("maintenance.reports.note")}</p>
          </Card>
        </>
      )}
    </>
  );
}
